import { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchAddReports,
  fetchCarLastMileageAfter,
  fetchCurrentReport,
  fetchReportGroupedCargos,
  fetchReports,
  pickLastTripPrefill,
  setCurrentReportId,
  setIsVisibleModalReport,
  setReportModalInitialTab,
} from '../../../redux/reducers/reports'
import { carsSelector, commercialCarsSelector } from '../../../redux/selectors/cars-selector'
import { reportsSelector } from '../../../redux/selectors/reports-selector'
import { AppDispatch } from '../../../redux/store'
import styles from './ReportModal.module.css'

import { fetchCars } from '../../../redux/reducers/cars'
import { fetchUsers } from '../../../redux/reducers/users'
import { driversAndAssistantsSelector } from '../../../redux/selectors/users-selector'
import DateTimePicker from '../../common/date-picker/DatePicker'

import { PATH } from '../../../constants/path'
import { useClearBodyNoScrollClass } from '../../../hooks/use-clear-body-no-scroll-slass'
import { getHost } from '../../../utils/getHost'
import {
  onlyIntegerCheck,
  onlyNumberRegularCheck,
} from '../../../utils/onlyNumberRegularCheck'
import {
  getReportPdfLinks,
  hasReportPdfLinks,
} from '../../../utils/reportPdfLinks'
import { ReportPdfMenuItems } from '../../report-pdf-menu/ReportPdfMenu'

import { useOutsideClick } from '../../../hooks/useOutsideClick'
import { fetchCities } from '../../../redux/reducers/cities'
import { setIsVisibleModalReportCommon } from '../../../redux/reducers/reports-common'
import { fetchSettings } from '../../../redux/reducers/settings'
import { citiesSelector } from '../../../redux/selectors/cities-selector'
import { reportsCommonSelector } from '../../../redux/selectors/reports-common-selector'
import { settingsSelector } from '../../../redux/selectors/settings-selector'
import { getRole } from '../../../utils/getRole'
import {
  isDraftStatus,
  isInTripStatus,
  isOpenReportStatus,
  isSentOrClosedStatus,
  REPORT_STATUS,
} from '../../../utils/reportStatus'
import CargosTo from './cargos/CargosTo'
import AllExpenses from './expenses/AllExpenses'
import AllIncomings from './incomings/AllIncomings'
import ReportCommonModal, { ReportCommonModalRef } from './ReportCommonModal'

function ReportModal() {
  const reportCommonModalRef = useRef<ReportCommonModalRef>(null)
  const dispatch = useDispatch<AppDispatch>()

  const {
    currentReport,
    currentReportId,
    lastReportsQuery,
    reportModalInitialTab,
    isVisibleModalReport,
    reports,
  } = useSelector(reportsSelector)
  const { currentReportTab } = useSelector(reportsCommonSelector)
  const { cities } = useSelector(citiesSelector)
  const { settings } = useSelector(settingsSelector)
  const { isDriver, userId } = getRole()
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const isReportsListPage = pathname === PATH.REPORTS
  const driverReportsReadOnlySent =
    isDriver && isReportsListPage && currentReport?.status === 'sent'
  const isDriverAssignedToCurrentReport =
    isDriver &&
    userId != null &&
    !!currentReport &&
    Number(currentReport.user?.id) === Number(userId)
  const isDriverOwnDraftOnReports =
    isReportsListPage &&
    isDriverAssignedToCurrentReport &&
    isOpenReportStatus(currentReport?.status)

  useClearBodyNoScrollClass()

  const { cars } = useSelector(carsSelector)
  const commercialCars = useSelector(commercialCarsSelector)
  // DATA
  const [user, setUser] = useState(currentReport?.user?.id || '')
  const [fuelBefore, setFuelBefore] = useState(currentReport?.fuel_before || '')
  const [fuelAfter, setFuelAfter] = useState(currentReport?.fuel_after || '')
  const [mileageAfter, setMileageAfter] = useState(
    currentReport?.mileage_after || ''
  )
  const [mileageBefore, setMileageBefore] = useState(
    currentReport?.mileage_before || ''
  )
  const [departure, setDeparture] = useState(currentReport?.departure || '')
  const [arrival, setArrival] = useState(currentReport?.arrival || '')

  const [crossingBorderBack, setCrossingBorderBack] = useState(
    currentReport?.crossing_border_back || ''
  )
  const [crossingBorderThere, setCrossingBorderThere] = useState(
    currentReport?.crossing_border_there || ''
  )
  const [reimbursementDate, setReimbursementDate] = useState(
    currentReport?.reimbursement_date || ''
  )
  const [trailer, setTrailer] = useState(currentReport?.trailer?.id || '')
  const [car, setCar] = useState(currentReport?.car?.id || '')
  // В путевом — только коммерческие; выбранные оставляем, чтобы не ломать старые отчёты
  const carsForReportSelect =
    car || trailer
      ? cars.filter(
          (item) =>
            item.commercial ||
            Number(item.id) === Number(car) ||
            Number(item.id) === Number(trailer)
        )
      : commercialCars
  const [status, setStatus] = useState(
    currentReport?.status || REPORT_STATUS.draft
  )
  const [avgCons, setAvgCons] = useState(currentReport?.avg_cons || '')

  const [issuedByn, setIssuedByn] = useState(currentReport?.issued_byn || '')
  const [issuedBynBalance, setIssuedBynBalance] = useState(
    currentReport?.issued_byn_balance || ''
  )
  const [issuedEur, setIssuedEur] = useState(currentReport?.issued_eur || '')
  const [issuedEurBalance, setIssuedEurBalance] = useState(
    currentReport?.issued_eur_balance || ''
  )
  const [issuedRub, setIssuedRub] = useState(currentReport?.issued_rub || '')
  const [issuedRubBalance, setIssuedRubBalance] = useState(
    currentReport?.issued_rub_balance || ''
  )
  const [issuedUsd, setIssuedUsd] = useState(currentReport?.issued_usd || '')
  const [issuedUsdBalance, setIssuedUsdBalance] = useState(
    currentReport?.issued_usd_balance || ''
  )

  const [number, setNumber] = useState(currentReport?.number || '')

  const [from, setFrom] = useState(currentReport?.from?.id)
  const [to, setTo] = useState(currentReport?.to?.id)
  const [jobTitle, setJobTitle] = useState(currentReport?.job_title || '')
  const [travelPurpose, setTravelPurpose] = useState(
    currentReport?.travel_purpose || ''
  )
  const [travelAllowance, setTravelAllowance] = useState(
    currentReport?.travel_allowance || ''
  )
  const settingsInitializedRef = useRef(false)
  const carChangeRequestIdRef = useRef(0)
  const appliedReportIdRef = useRef<number | string | null | undefined>(
    undefined
  )

  // TIMEX-353: «В рейсе» — только машины с can_create_report из GET /cars.
  // Черновики флаг не занимают. Свою машину текущего «В рейсе» оставляем в списке.
  const isOwnInTripVehicle = (vehicleId: number) => {
    if (!currentReport?.id || !isInTripStatus(currentReport.status)) return false
    if (
      currentReport.car?.id &&
      Number(vehicleId) === Number(currentReport.car.id)
    ) {
      return true
    }
    if (
      currentReport.trailer?.id &&
      Number(vehicleId) === Number(currentReport.trailer.id)
    ) {
      return true
    }
    return false
  }

  const isVehicleBusyInTrip = (vehicleId?: string | number | null) => {
    if (vehicleId === '' || vehicleId == null) return false
    if (isOwnInTripVehicle(Number(vehicleId))) return false
    const vehicle = cars.find((item) => Number(item.id) === Number(vehicleId))
    return vehicle?.can_create_report === false
  }
  useEffect(() => {
    const reportId = currentReportId ?? null
    const reportMatchesId =
      !reportId ||
      (currentReport != null &&
        String(currentReport.id) === String(reportId))

    if (reportId && !reportMatchesId) {
      return
    }

    if (appliedReportIdRef.current === reportId && reportId != null) {
      return
    }
    appliedReportIdRef.current = reportId

    // Всегда сначала сбрасываем все поля, чтобы не оставались старые значения
    setUser('')
    setFuelBefore('')
    setFuelAfter('')
    setMileageAfter('')
    setMileageBefore('')
    setDeparture('')
    setArrival('')
    setCrossingBorderBack('')
    setCrossingBorderThere('')
    setReimbursementDate('')
    setTrailer('')
    setCar('')
    setStatus(REPORT_STATUS.draft)
    setAvgCons('')
    setIssuedByn('')
    setIssuedBynBalance('')
    setIssuedEur('')
    setIssuedEurBalance('')
    setIssuedRub('')
    setIssuedRubBalance('')
    setIssuedUsd('')
    setIssuedUsdBalance('')
    setNumber('')
    setFrom(undefined)
    setTo(undefined)
    setJobTitle('')
    setTravelPurpose('')
    setTravelAllowance('')

    // Затем заполняем значениями из currentReport, если он есть
    if (currentReport) {
      setUser(currentReport.user?.id || '')
      setFuelBefore(
        isDraftStatus(currentReport.status) ? '' : currentReport.fuel_before || ''
      )
      setFuelAfter(
        isDraftStatus(currentReport.status) ? '' : currentReport.fuel_after || ''
      )
      setCar(currentReport.car?.id || '')
      setMileageAfter(
        currentReport.car?.id && !isDraftStatus(currentReport.status)
          ? currentReport.mileage_after || ''
          : ''
      )
      setMileageBefore(
        currentReport.car?.id && !isDraftStatus(currentReport.status)
          ? currentReport.mileage_before || ''
          : ''
      )
      setDeparture(currentReport.departure || '')
      setArrival(currentReport.arrival || '')
      setCrossingBorderBack(currentReport.crossing_border_back || '')
      setCrossingBorderThere(currentReport.crossing_border_there || '')
      setReimbursementDate(currentReport.reimbursement_date || '')
      setTrailer(currentReport.trailer?.id || '')
      setStatus(currentReport.status)
      setAvgCons(currentReport.avg_cons || '')
      setIssuedByn(currentReport.issued_byn || '')
      setIssuedBynBalance(currentReport.issued_byn_balance || '')
      setIssuedEur(currentReport.issued_eur || '')
      setIssuedEurBalance(currentReport.issued_eur_balance || '')
      setIssuedRub(currentReport.issued_rub || '')
      setIssuedRubBalance(currentReport.issued_rub_balance || '')
      setIssuedUsd(currentReport.issued_usd || '')
      setIssuedUsdBalance(currentReport.issued_usd_balance || '')
      setNumber(currentReport.number || '')
      setFrom(currentReport?.from?.id)
      setTo(currentReport?.to?.id)
      setJobTitle(currentReport?.job_title || '')
      setTravelPurpose(currentReport?.travel_purpose || '')
      setTravelAllowance(currentReport?.travel_allowance || '')
    }
  }, [currentReport, currentReportId])

  useEffect(() => {
    if (!currentReport) return
    setIssuedBynBalance(currentReport.issued_byn_balance ?? '')
    setIssuedRubBalance(currentReport.issued_rub_balance ?? '')
    setIssuedEurBalance(currentReport.issued_eur_balance ?? '')
    setIssuedUsdBalance(currentReport.issued_usd_balance ?? '')
  }, [
    currentReport?.issued_byn_balance,
    currentReport?.issued_rub_balance,
    currentReport?.issued_eur_balance,
    currentReport?.issued_usd_balance,
  ])

  const isNewReport = !!currentReport
  const isEditMyReport = pathname === PATH.MY_REPORTS

  // my_report && existing report && open status
  const isEditDraftReport =
    isEditMyReport && isNewReport && isOpenReportStatus(currentReport?.status)
  const isDriverDraftLikeMyReports =
    isEditDraftReport || isDriverOwnDraftOnReports
  const useMyReportsSentPayload = isEditMyReport || isDriverOwnDraftOnReports

  const isDraftForm = isDraftStatus(status) || (!currentReport && !status)
  const isInTripForm = isInTripStatus(status)

  // TIMEX-351/353: машину можно менять в черновике, «В рейсе» и при создании
  const canEditCar =
    !currentReport || isOpenReportStatus(currentReport.status)
  const hasCar = Boolean(car)
  const canEditMileageBefore = false
  const canEditMileageAfter = hasCar && isInTripForm
  const canEditFuelBefore = false
  const canEditFuelAfter = hasCar && isInTripForm

  const isFuelBeforeRequired = (statusValue?: string | null) =>
    isSentOrClosedStatus(statusValue)

  const applyLastTripPrefill = (carId: string) => {
    const requestId = ++carChangeRequestIdRef.current
    const prefillArgs = {
      carId,
      excludeReportId: currentReport?.id,
      beforeArrival: arrival || departure || null,
    }
    const fromStore = pickLastTripPrefill(reports || [], prefillArgs)
    setMileageBefore(
      fromStore.mileage_after != null ? String(fromStore.mileage_after) : ''
    )
    setFuelBefore(
      fromStore.fuel_after != null ? String(fromStore.fuel_after) : ''
    )

    dispatch(fetchCarLastMileageAfter(prefillArgs)).then((result) => {
      if (requestId !== carChangeRequestIdRef.current) return
      if (!fetchCarLastMileageAfter.fulfilled.match(result)) {
        setMileageBefore('')
        setFuelBefore('')
        return
      }
      const { mileage_after, fuel_after } = result.payload
      setMileageBefore(
        mileage_after !== null && mileage_after !== undefined
          ? String(mileage_after)
          : ''
      )
      setFuelBefore(
        fuel_after !== null && fuel_after !== undefined
          ? String(fuel_after)
          : ''
      )
    })
  }

  const clearMileageAndFuel = () => {
    setMileageBefore('')
    setMileageAfter('')
    setFuelBefore('')
    setFuelAfter('')
  }

  const handleCarChange = (value: string) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      car: false,
      mileageBefore: false,
      mileageAfter: false,
    }))
    setCar(value)

    if (!value) {
      clearMileageAndFuel()
      return
    }

    if (isInTripForm && isVehicleBusyInTrip(value)) {
      setCar('')
      clearMileageAndFuel()
      return
    }

    if (trailer && String(trailer) === String(value)) {
      setTrailer('')
    }

    if (isDraftForm) {
      clearMileageAndFuel()
      return
    }

    clearMileageAndFuel()
    applyLastTripPrefill(value)
  }

  const handleStatusChange = (nextStatus: string) => {
    const prevStatus = status
    setStatus(nextStatus)

    if (isDraftStatus(nextStatus)) {
      clearMileageAndFuel()
      return
    }

    if (isInTripStatus(nextStatus) && !isInTripStatus(prevStatus)) {
      dispatch(fetchCars())

      if (isVehicleBusyInTrip(car)) {
        setCar('')
        clearMileageAndFuel()
      } else if (car) {
        applyLastTripPrefill(String(car))
      }

      if (isVehicleBusyInTrip(trailer)) {
        setTrailer('')
      }
    }
  }

  // Load settings for new report
  useEffect(() => {
    if (!currentReportId && !currentReport) {
      dispatch(fetchSettings())
    }
  }, [currentReportId, currentReport, dispatch])

  // Set default values from settings when creating new report (only once)
  useEffect(() => {
    if (
      !currentReportId &&
      !currentReport &&
      settings &&
      settings.length > 0 &&
      !settingsInitializedRef.current
    ) {
      const jobTitleSetting = settings.find((s) => s.slug === 'jobTitle')
      const travelPurposeSetting = settings.find(
        (s) => s.slug === 'travelPurpose'
      )

      if (jobTitleSetting && jobTitleSetting.value) {
        setJobTitle(String(jobTitleSetting.value))
      }
      if (travelPurposeSetting && travelPurposeSetting.value) {
        setTravelPurpose(String(travelPurposeSetting.value))
      }

      // Set travel allowance from settings
      const travelAllowanceSetting = settings.find(
        (s: any) => s.slug === 'travelAllowances'
      )
      if (
        travelAllowanceSetting &&
        travelAllowanceSetting.value &&
        !travelAllowance
      ) {
        setTravelAllowance(String(travelAllowanceSetting.value))
      }

      settingsInitializedRef.current = true
    }

    // Reset flag when report is loaded or modal is closed
    if (currentReportId || currentReport) {
      settingsInitializedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, currentReportId, currentReport])

  const [errors, setErrors] = useState<{
    departure: boolean

    mileageBefore: boolean
    car: boolean
    // trailer: boolean
    fuelBefore: boolean

    mileageAfter?: boolean
    fuelAfter?: boolean
    arrival: boolean
    from: boolean
    to: boolean

    user?: boolean
  }>({
    departure: false,
    mileageBefore: false,
    car: false,
    // trailer: false,
    fuelBefore: false,

    mileageAfter: false,
    fuelAfter: false,
    arrival: false,
    from: false,
    to: false,
    user: false,
  })

  const checkValidation = () => {
    const fuelBeforeRequired = isFuelBeforeRequired(currentReport?.status)
    let newErrors = {
      departure: !departure,
      // TIMEX-351: при сохранении черновика грузовик и пробег до необязательны
      mileageBefore: false,
      car: false,
      fuelBefore: fuelBeforeRequired ? !fuelBefore : false,
      // trailer: !trailer,

      mileageAfter: false,
      fuelAfter: false,
      arrival: !arrival,
      from: !from,
      to: !to,
      user: false,
    }

    if (!!currentReport?.id && isSentOrClosedStatus(currentReport.status)) {
      newErrors = {
        ...newErrors,
        mileageBefore: !mileageBefore,
        car: !car,
        fuelBefore: !fuelBefore,
        mileageAfter: !mileageAfter,
        fuelAfter: !fuelAfter,
        arrival: !arrival,
        user: !user,
        from: !from,
        to: !to,
      }
    }
    setErrors(newErrors)
  }
  const handleSaveReport = (e: any, statusReport?: string) => {
    e.preventDefault()
    const commonParams = {
      departure,
      mileageBefore,
      car,
      fuelBefore,
      fuelAfter,
      mileageAfter,
      trailer,
      crossingBorderThere,
      crossingBorderBack,
      avgCons,
      reimbursementDate,
      status: statusReport || status,
      issuedByn,
      issuedEur,
      issuedRub,
      issuedUsd,
      user,
      arrival,
      number,
      from,
      to,
      jobTitle,
      travelPurpose,
      travelAllowance,
    }

    const nextStatus = statusReport || status
    const isDraftSave = !nextStatus || isDraftStatus(nextStatus)
    const isCarMissing = isDraftSave ? false : !car
    const isCarBusy =
      isInTripStatus(nextStatus) && isVehicleBusyInTrip(car)

    const newErrors = {
      departure: !departure,
      mileageBefore: false,
      car: isCarMissing || isCarBusy,
      fuelBefore: isFuelBeforeRequired(nextStatus) ? !fuelBefore : false,
      from: !from,
      to: !to,
      arrival: !arrival,
    }

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error)

    if (!hasErrors) {
      if (currentReportId) {
        dispatch(
          fetchAddReports({ ...commonParams, id: currentReportId })
        ).then((result: any) => {
          if (result.type === 'reports/fetchAddReports/fulfilled') {
            dispatch(fetchReports(lastReportsQuery))
            dispatch(fetchCars())
          }
        })
      } else {
        dispatch(fetchAddReports(commonParams)).then((result: any) => {
          // Обновляем список путевых при успешном создании нового путевого
          if (result.type === 'reports/fetchAddReports/fulfilled') {
            dispatch(fetchReports(lastReportsQuery))
            dispatch(fetchCars())
          }
        })
      }
    }
  }

  const handleSentReport = (e: any) => {
    e.preventDefault()
    if (isDriver && isReportsListPage && !isDriverOwnDraftOnReports) {
      setIsVisibleConfirmModal(false)
      return
    }
    const commonParams = {
      departure,
      mileageBefore,
      car,
      fuelBefore,
      fuelAfter,
      mileageAfter,
      trailer,
      crossingBorderThere,
      crossingBorderBack,
      reimbursementDate,
      status: useMyReportsSentPayload ? 'sent' : status,
      arrival,
      avgCons,
      user: !useMyReportsSentPayload ? user : null,
      issuedByn,
      issuedEur,
      issuedRub,
      issuedUsd,
      number,
      from,
      to,
      jobTitle,
      travelPurpose,
      travelAllowance,
    }

    const newErrors = {
      departure: !departure,
      mileageBefore: !mileageBefore,
      car: !car,
      fuelBefore: !fuelBefore,

      mileageAfter: !mileageAfter,
      fuelAfter: !fuelAfter,
      arrival: !arrival,
      from: !from,
      to: !to,
      // crossingBorderThere: !crossingBorderThere,
      // crossingBorderBack: !crossingBorderBack,
    }
    if (!useMyReportsSentPayload) {
      //@ts-ignore
      newErrors.user = !user
    }

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error)

    if (hasErrors) {
      setIsVisibleConfirmModal(false)
    }
    if (!hasErrors) {
      dispatch(
        fetchAddReports({
          ...commonParams,
          id: currentReportId,
          user: user ? user : 'ME',
        })
      ).then((result: any) => {
        // Обновляем список путевых при успешном обновлении/отправке путевого
        if (result.type === 'reports/fetchAddReports/fulfilled') {
          dispatch(fetchReports(lastReportsQuery))
          dispatch(fetchCars())
        }
      })
    }
  }

  const handleOpenConfirmModal = (e: any) => {
    const newErrors = {
      departure: !departure,
      mileageBefore: !mileageBefore,
      car: !car,
      fuelBefore: !fuelBefore,

      mileageAfter: !mileageAfter,
      fuelAfter: !fuelAfter,
      arrival: !arrival,
      user: useMyReportsSentPayload ? false : !user,
      from: !from,
      to: !to,
      // crossingBorderThere: !crossingBorderThere,
      // crossingBorderBack: !crossingBorderBack,
    }

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error)
    if (hasErrors) {
      setIsVisibleConfirmModal(false)
    }
    if (!hasErrors) {
      setIsVisibleConfirmModal(true)
    }
  }

  useEffect(() => {
    if (currentReportTab === '1') {
      if (!isDriver) {
        dispatch(fetchUsers())
      }
      dispatch(fetchCars())
      dispatch(fetchCities())
    }
  }, [currentReportTab, dispatch, isDriver])

  useEffect(() => {
    if (isVisibleModalReport) {
      dispatch(fetchCars())
    }
  }, [isVisibleModalReport, dispatch])

  useEffect(() => {
    if (!isInTripStatus(status)) return
    if (car && isVehicleBusyInTrip(car)) {
      setCar('')
      clearMileageAndFuel()
    }
    if (trailer && isVehicleBusyInTrip(trailer)) {
      setTrailer('')
    }
    // После обновления GET /cars занятые машины снимаем с формы «В рейсе»
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars, status, car, trailer])

  const driversAndAssistants = useSelector(driversAndAssistantsSelector)
  const driverSelectOptions = isDriver
    ? [
      {
        id: 'ME',
        name: currentReport?.user?.name || 'Текущий водитель',
      },
    ]
    : driversAndAssistants

  useEffect(() => {
    if (currentReportId) {
      dispatch(fetchCurrentReport(currentReportId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentReportId])

  const reportIdForGroupedCargos =
    currentReportId != null && currentReportId !== ''
      ? currentReportId
      : currentReport?.id != null
        ? currentReport.id
        : null

  const [isVisibleConfirmModal, setIsVisibleConfirmModal] = useState(false)

  function formatNumber(number: number) {
    let roundedNumber = number.toFixed(2)
    if (roundedNumber.endsWith('.00')) {
      return Math.floor(number).toString()
    } else {
      return roundedNumber
    }
  }

  function ConfirmModal() {
    return (
      <div style={{ display: 'block' }} className={styles.win}>
        <div className={styles.winContainer}>
          <div className={styles.winContent}>
            <div
              onClick={() => setIsVisibleConfirmModal(false)}
              className={styles.close}
            >
              ×
            </div>
            <div className={styles.winContentFulltext}>
              <h3
                style={{ textAlign: 'center' }}
                className={classNames(styles.h3)}
              >
                Подать путевой?
              </h3>
              <div
                className={classNames(
                  'row',
                  styles.small2,
                  styles.borderRadius5,
                  styles.rowNote,
                  styles.bxShadow
                )}
              >
                <div className={classNames(styles.confirmText)}>
                  Отправив путевой, вы уже не сможете его смотреть,
                  редактировать или удалить.
                </div>
              </div>
              <div className="row mt-3">
                <div
                  className="col-6 col-sm-6 mt-1 mb-1"
                  style={{ textAlign: 'left' }}
                >
                  <button
                    className={classNames(
                      'd-block',
                      'd-sm-inline-block',
                      styles.btn,
                      styles.btnSmall
                    )}
                    onClick={(e) => {
                      e.preventDefault()
                      handleSentReport(e)
                    }}
                  >
                    <i className={`${styles.icon} ${styles.iconCheckWhite}`} />{' '}
                    Отправить
                  </button>
                </div>
                <div
                  className="col-6 col-sm-6 mt-1 mb-1"
                  style={{ textAlign: 'right' }}
                >
                  <div
                    onClick={() => setIsVisibleConfirmModal(false)}
                    className={classNames(
                      'd-block',
                      'd-sm-inline-block',
                      styles.btn,
                      styles.btnSmall,
                      styles.btnBlueOutline
                    )}
                  >
                    Отмена
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const [isVisibleDownloadList, setIsVisibleDownloadList] = useState(false)
  const ref = useOutsideClick(() => {
    setIsVisibleDownloadList(false)
  })

  useEffect(() => {
    //here
    const getAllExpensesByFuel = currentReport?.expenses
      .filter((item) => item.type === 'fuel')
      .reduce((sum, item) => sum + (item?.quantity || 0), 0)

    //@ts-ignore
    if (
      mileageBefore &&
      mileageAfter &&
      fuelBefore !== undefined &&
      fuelAfter !== undefined
    ) {
      const mileage = Number(mileageAfter) - Number(mileageBefore)

      const fuel =
        (getAllExpensesByFuel || 0) + Number(fuelBefore) - Number(fuelAfter)

      // if (fuel > 0) {
      const avgConsumption = fuel / mileage
      setAvgCons((Number(avgConsumption) * 100).toFixed(3))
      // }
    }
  }, [fuelAfter, mileageAfter, currentReport])

  const [currentTab, setCurrentTab] = useState<number>(1)
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    if (reportModalInitialTab == null) return
    setCurrentTab(reportModalInitialTab)
    dispatch(setReportModalInitialTab(null))
  }, [dispatch, reportModalInitialTab])

  useEffect(() => {
    if (reportIdForGroupedCargos == null) return
    if (currentTab === 4) {
      void dispatch(
        fetchReportGroupedCargos({
          reportId: reportIdForGroupedCargos,
          direction: 'forward',
        })
      )
    } else if (currentTab === 5) {
      void dispatch(
        fetchReportGroupedCargos({
          reportId: reportIdForGroupedCargos,
          direction: 'backward',
        })
      )
    }
  }, [dispatch, reportIdForGroupedCargos, currentTab])

  return (
    <div style={{ display: 'block' }} className={styles.win}>
      {isVisibleConfirmModal && <ConfirmModal />}

      <div className={styles.winContainer}>
        <div className={styles.winContent}>
          <div
            onClick={() => {
              dispatch(setIsVisibleModalReportCommon(false))
              dispatch(setIsVisibleModalReport(false))
              dispatch(setCurrentReportId(null))
            }}
            className={styles.close}
          >
            ×
          </div>
          {currentReportTab === '1' ? (
            <div className={styles.winContentFulltext}>
              <h3
                style={{ textAlign: 'left' }}
                className={classNames(styles.h3, 'mb-2 mt-4 pl-0 pr-0 w-100')}
              >
                {currentReport
                  ? `Путевой ${currentReport?.number ? '№ ' + currentReport?.number : 'Б/Н'}`
                  : 'Новый путевой'}
              </h3>
              {!!currentReport && (
                <div className="row align-items-center mb-3">
                  <div className="col-7">
                    {!isDriver && (
                      <span
                        className={classNames(
                          styles.small2,
                          styles.mr25,
                          'd-block d-sm-inline-block'
                        )}
                      >
                        <span
                          className={classNames(
                            styles.small3,
                            styles.colorGray
                          )}
                        >
                          Грузы:{' '}
                        </span>
                        <span>
                          <b
                            className={classNames(
                              styles.colorGreen,
                              styles.nowrap
                            )}
                          >
                            {formatNumber(
                              Number(currentReport.cargos_total || 0)
                            )}{' '}
                            р.
                          </b>
                        </span>
                      </span>
                    )}

                    {!isDriver &&
                      [
                        {
                          key: 'bn',
                          label: 'Б/Н:',
                          value: Number(currentReport.cargos_total_bank || 0),
                        },
                        {
                          key: 'cash',
                          label: 'Н:',
                          value: Number(currentReport.cargos_total_cash || 0),
                        },
                      ].map((item) => (
                        <span
                          key={item.key}
                          className={classNames(
                            styles.small2,
                            styles.mr25,
                            'd-inline-block'
                          )}
                        >
                          <span
                            className={classNames(
                              styles.small3,
                              styles.colorGray
                            )}
                          >
                            {item.label}{' '}
                          </span>
                          <span
                            className={classNames(
                              styles.small3,
                              styles.nowrap
                            )}
                          >
                            {formatNumber(item.value)} р.
                          </span>
                        </span>
                      ))}

                    <span className={classNames(styles.small2, 'd-block')}>
                      <span
                        className={classNames(styles.small3, styles.colorGray)}
                      >
                        Расходы:{' '}
                      </span>
                      <span>
                        <b
                          className={classNames(
                            styles.colorRed,
                            styles.nowrap
                          )}
                        >
                          {formatNumber(
                            Number(currentReport.all_expenses || 0)
                          )}{' '}
                          р.
                        </b>
                      </span>
                    </span>
                  </div>
                  {!isEditMyReport && (
                    <div className="col-5" style={{ textAlign: 'right' }}>
                      {hasReportPdfLinks(currentReport) && (
                        <div
                          //@ts-ignore
                          ref={ref}
                          onClick={() =>
                            setIsVisibleDownloadList((prev) => !prev)
                          }
                          className={classNames(
                            styles.dropdown,
                            styles.dropdownLeft
                          )}
                        >
                          <div
                            className={classNames(
                              styles.btn,
                              styles.btnSmall,
                              styles.btnOutline,
                              styles.btnIcon,
                              styles.btnDropdown
                            )}
                          >
                            <i
                              className={classNames(
                                styles.icon,
                                styles.iconDownload
                              )}
                            />
                          </div>
                          {isVisibleDownloadList && (
                            <div
                              className={classNames(
                                styles.dropdownContent,
                                styles.small2
                              )}
                            >
                              <ReportPdfMenuItems
                                items={getReportPdfLinks(currentReport)}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {currentReport?.report_images_url && (
                        <a
                          className={classNames(
                            styles.btn,
                            styles.btnSmall2,
                            styles.btnBlueOutline,
                            styles.btnIcon
                          )}
                          target="_blank"
                          href={`${getHost()}${currentReport?.report_images_url}`}
                          rel="noreferrer"
                        >
                          <i
                            className={classNames(
                              styles.icon,
                              styles.iconPicture,
                              styles.iconPictureWithoutPadding
                            )}
                          />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
              {!!currentReport && (
                <div
                  className={classNames(
                    'd-flex flex-wrap align-items-end',
                    styles.radioTabsHead,
                    styles.small
                  )}
                >
                  <div
                    className={classNames(
                      styles.menuButton,
                      currentTab === 1 ? styles.menuButtonActive : '',
                      ''
                    )}
                    onClick={() => setCurrentTab(1)}
                  >
                    Основное
                  </div>
                  <div
                    className={classNames(
                      styles.menuButton,
                      currentTab === 2 ? styles.menuButtonActive : ''
                      // 'col-4 col-sm-4 col-md-4 mt-2'
                    )}
                    onClick={() => setCurrentTab(2)}
                  >
                    Расходы
                  </div>
                  <div
                    className={classNames(
                      styles.menuButton,
                      currentTab === 3 ? styles.menuButtonActive : ''
                      // 'col-4 col-sm-4 col-md-4 mt-3'
                    )}
                    onClick={() => setCurrentTab(3)}
                  >
                    Приходы
                  </div>

                  <div
                    className={classNames(
                      styles.menuButton,
                      currentTab === 4 ? styles.menuButtonActive : ''
                      // 'col-4 col-sm-4 col-md-4 mt-3'
                    )}
                    onClick={() => setCurrentTab(4)}
                  >
                    Грузы туда
                  </div>

                  <div
                    className={classNames(
                      styles.menuButton,
                      currentTab === 5 ? styles.menuButtonActive : ''
                      // 'col-4 col-sm-4 col-md-4 mt-3'
                    )}
                    onClick={() => setCurrentTab(5)}
                  >
                    Грузы обратно
                  </div>
                </div>
              )}

              {currentTab === 1 && (
                <form
                  ref={formRef}
                  onSubmit={(e) => {
                    if (driverReportsReadOnlySent) {
                      e.preventDefault()
                      return
                    }
                    if (
                      isSentOrClosedStatus(status) &&
                      !useMyReportsSentPayload
                    ) {
                      handleSentReport(e)
                    } else {
                      handleSaveReport(e, status || REPORT_STATUS.draft)
                    }
                  }}
                >
                  <fieldset
                    disabled={driverReportsReadOnlySent}
                    style={{
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      minWidth: 0,
                    }}
                  >
                    {!isEditMyReport && !isDriver && (
                      <div
                        className={classNames(
                          styles.bxShadowSmall,
                          styles.bgGray,
                          styles.border10,
                          styles.small2,
                          styles.docItem,
                          'pt-2 pb-2 pl-3 pr-3 mb-3 '
                        )}
                      >
                        <div
                          className={classNames(
                            styles.row,
                            'row',
                            'align-items-start'
                          )}
                        >
                          <div className="col-6 col-sm-2 mt-1 mb-1">
                            <small
                              className={classNames(
                                styles.small2,
                                'd-block mb-1'
                              )}
                            >
                              Номер П/Л
                            </small>
                            <input
                              value={number}
                              onChange={(e) => {
                                setNumber(e.target.value)
                              }}
                              type="text"
                            />
                          </div>

                          <div className="col-6 col-sm-4 mt-1 mb-1">
                            <small
                              className={classNames(
                                styles.small2,
                                'd-block mb-1'
                              )}
                            >
                              Водитель
                            </small>
                            <select
                              value={user}
                              onChange={(e) => {
                                setUser(e.target.value)
                                setErrors((prevErrors) => ({
                                  ...prevErrors,
                                  user: false,
                                }))
                              }}
                              className={classNames(
                                errors.user ? styles.isError : ''
                              )}
                            >
                              {/* <option value="" key={undefined}>
                              — Выберите —
                            </option> */}
                              {driverSelectOptions.map((user) => {
                                return (
                                  <option key={user.id} value={user.id}>
                                    {user.name}
                                  </option>
                                )
                              })}
                            </select>
                          </div>

                          <div className="col-6 col-sm-3 mt-1 mb-1">
                            <small
                              className={classNames(
                                styles.small2,
                                'd-block mb-1'
                              )}
                            >
                              Ср. расход, л/100
                            </small>
                            <input
                              className={styles.disableInput}
                              value={
                                avgCons && isFinite(+avgCons) && +avgCons > 0
                                  ? Number(avgCons).toFixed(2)
                                  : 0
                              }
                              onChange={(e) => setAvgCons(e.target.value)}
                              type="text"
                              disabled={true}
                            />
                          </div>
                          <div className="col-6 col-sm-3 mt-1 mb-1">
                            <small
                              className={classNames(
                                styles.small2,
                                'd-block mb-1'
                              )}
                            >
                              Статус
                            </small>
                            <select
                              value={status || REPORT_STATUS.draft}
                              onChange={(e) => handleStatusChange(e.target.value)}
                            >
                              <option key="draft" value={REPORT_STATUS.draft}>
                                Черновик
                              </option>
                              <option key="in_trip" value={REPORT_STATUS.inTrip}>
                                В рейсе
                              </option>
                              <option key="sent" value={REPORT_STATUS.sent}>
                                Отправлен
                              </option>
                              <option key="closed" value={REPORT_STATUS.closed}>
                                Закрыт
                              </option>
                            </select>
                          </div>
                        </div>
                        <div
                          className={classNames(
                            styles.row,
                            'row',
                            'align-items-start'
                          )}
                        >
                          <div className="col-6 col-sm-6 mt-1 mb-1">
                            <small
                              className={classNames(
                                styles.small2,
                                'd-block mb-1'
                              )}
                            >
                              Должность
                            </small>
                            <input
                              value={jobTitle}
                              onChange={(e) => setJobTitle(e.target.value)}
                              type="text"
                            />
                          </div>
                          <div className="col-6 col-sm-6 mt-1 mb-1">
                            <small
                              className={classNames(
                                styles.small2,
                                'd-block mb-1'
                              )}
                            >
                              Цель
                            </small>
                            <input
                              value={travelPurpose}
                              onChange={(e) => setTravelPurpose(e.target.value)}
                              type="text"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-12 col-sm-6 col-md-6 col-lg-6 order-1">
                        <div className="row">
                          <div className="col-12 mb-3 pr-1">
                            <label
                              htmlFor="report_date_input_departure"
                              className={classNames(
                                styles.small2,
                                styles.label,
                                'd-block mb-1'
                              )}
                            >
                              Дата и время выезда*
                            </label>
                            <div
                              className={classNames(
                                styles.reportDate,
                                styles.border5,
                                errors.departure ? styles.isError : ''
                              )}
                              onClick={() => {
                                setErrors((prevErrors) => ({
                                  ...prevErrors,
                                  departure: false,
                                }))
                              }}
                              id="report_date_input_wrapper1"
                            >
                              <DateTimePicker
                                id="report_date_input_departure"
                                selectedDate={departure}
                                setSelectedDate={setDeparture}
                                isValidateDate={false}
                              />
                            </div>
                          </div>
                          <div className="col-12 mb-3 pr-1">
                            <label
                              htmlFor="report_date_input_arrival"
                              className={classNames(
                                styles.small2,
                                styles.label,
                                'd-block mb-1'
                              )}
                            >
                              Дата и время приезда*
                            </label>
                            <div
                              className={classNames(
                                styles.reportDate,
                                styles.border5,
                                errors.arrival ? styles.isError : ''
                              )}
                              onClick={() => {
                                setErrors((prevErrors) => ({
                                  ...prevErrors,
                                  arrival: false,
                                }))
                              }}
                              id="report_date_input_wrapper_back"
                            >
                              <DateTimePicker
                                id="report_date_input_arrival"
                                selectedDate={arrival}
                                setSelectedDate={setArrival}
                                isValidateDate={false}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-sm-6 col-md-6 col-lg-6 order-3 order-sm-2">
                        <div className="row">
                          <div className="col-6 mb-3 pr-1">
                            <small
                              className={classNames(
                                styles.small2,
                                'd-block mb-1'
                              )}
                            >
                              Пробег До, км
                            </small>
                            <input
                              className={`${errors.mileageBefore ? styles.isError : ''} ${!canEditMileageBefore || isDraftForm ? styles.disableInput : ''}`}
                              disabled={!canEditMileageBefore || isDraftForm}
                              value={hasCar && !isDraftForm ? mileageBefore : ''}
                              onChange={(e) => {
                                setErrors((prevErrors) => ({
                                  ...prevErrors,
                                  mileageBefore: false,
                                }))

                                if (onlyIntegerCheck(e)) {
                                  setMileageBefore(e.target.value)
                                }
                              }}
                              type="text"
                            />
                          </div>
                          <div className="col-6 mb-3 pl-1">
                            <small
                              className={classNames(
                                styles.small2,
                                'd-block mb-1'
                              )}
                            >
                              Пробег После, км
                            </small>
                            <input
                              className={`${errors.mileageAfter ? styles.isError : ''} ${!canEditMileageAfter ? styles.disableInput : ''}`}
                              disabled={!canEditMileageAfter}
                              value={hasCar && !isDraftForm ? mileageAfter : ''}
                              onChange={(e) => {
                                setErrors((prevErrors) => ({
                                  ...prevErrors,
                                  mileageAfter: false,
                                }))

                                if (onlyIntegerCheck(e)) {
                                  setMileageAfter(e.target.value)
                                }
                              }}
                              type="text"
                            />
                          </div>
                          <div className="col-6 mb-3 pr-1">
                            <small
                              className={classNames(
                                styles.small2,
                                'd-block mb-1'
                              )}
                            >
                              Топливо До, л
                            </small>
                            <input
                              className={`${errors.fuelBefore ? styles.isError : ''} ${!canEditFuelBefore || isDraftForm ? styles.disableInput : ''}`}
                              disabled={!canEditFuelBefore || isDraftForm}
                              value={isDraftForm ? '' : fuelBefore}
                              onChange={(e) => {
                                setErrors((prevErrors) => ({
                                  ...prevErrors,
                                  fuelBefore: false,
                                }))

                                if (onlyNumberRegularCheck(e)) {
                                  setFuelBefore(
                                    e.target.value.replace(',', '.')
                                  )
                                }
                              }}
                              type="text"
                            />
                          </div>
                          <div className="col-6 mb-3 pl-1">
                            <small
                              className={classNames(
                                styles.small2,
                                'd-block mb-1'
                              )}
                            >
                              Топливо После, л
                            </small>
                            <input
                              className={`${errors.fuelAfter ? styles.isError : ''} ${!canEditFuelAfter ? styles.disableInput : ''}`}
                              disabled={!canEditFuelAfter}
                              value={isDraftForm ? '' : fuelAfter}
                              onChange={(e) => {
                                setErrors((prevErrors) => ({
                                  ...prevErrors,
                                  fuelAfter: false,
                                }))

                                if (onlyNumberRegularCheck(e)) {
                                  setFuelAfter(e.target.value.replace(',', '.'))
                                }
                              }}
                              type="text"
                            />
                          </div>
                        </div>
                      </div>

                      <div
                        className={classNames(
                          styles.mobilePaddingRight,
                          'col-6 col-sm-6 mb-3 order-2 order-sm-3'
                        )}
                      >
                        <small
                          className={classNames(styles.small2, 'd-block mb-1')}
                        >
                          Грузовик
                        </small>
                        <select
                          className={`${errors.car ? styles.isError : ''} ${!canEditCar ? styles.disableInput : ''}`}
                          value={car}
                          disabled={!canEditCar}
                          onChange={(e) => handleCarChange(e.target.value)}
                        >
                          <option value="">— Выберите —</option>
                          {Boolean(carsForReportSelect.length) ? (
                            carsForReportSelect.map((item) => {
                              const busy =
                                isInTripForm && isVehicleBusyInTrip(item.id)
                              const sameAsTrailer =
                                Boolean(trailer) &&
                                Number(item.id) === Number(trailer)
                              const disabled = busy || sameAsTrailer
                              return (
                                <option
                                  key={item.id}
                                  value={item.id}
                                  disabled={disabled}
                                >
                                  {busy
                                    ? `${item.name} — уже в рейсе`
                                    : item.name}
                                </option>
                              )
                            })
                          ) : (
                            <option value="" disabled>
                              Нет доступных машин
                            </option>
                          )}
                        </select>
                      </div>
                      <div
                        className={classNames(
                          styles.mobilePaddingLeft,
                          'col-6 col-sm-6 mb-3 order-2 order-sm-3'
                        )}
                      >
                        <small
                          className={classNames(styles.small2, 'd-block mb-1')}
                        >
                          Прицеп
                        </small>
                        <select
                          value={trailer}
                          onChange={(e) => {
                            const nextTrailer = e.target.value
                            if (
                              isInTripForm &&
                              isVehicleBusyInTrip(nextTrailer)
                            ) {
                              setTrailer('')
                              return
                            }
                            if (car && String(nextTrailer) === String(car)) {
                              setTrailer('')
                              return
                            }
                            setTrailer(nextTrailer)
                          }}
                        >
                          <option value="" key={undefined}>
                            — Выберите —
                          </option>
                          {carsForReportSelect.map((item) => {
                            const busy =
                              isInTripForm && isVehicleBusyInTrip(item.id)
                            const sameAsTruck =
                              Boolean(car) && Number(item.id) === Number(car)
                            const disabled = busy || sameAsTruck
                            return (
                              <option
                                key={item.id}
                                value={item.id}
                                disabled={disabled}
                              >
                                {busy
                                  ? `${item.name} — уже в рейсе`
                                  : item.name}
                              </option>
                            )
                          })}
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-sm-6 col-md-6 col-lg-6 mb-3">
                        <small className="small-2 d-block mb-1">Откуда</small>
                        <select
                          className={errors.from ? styles.isError : ''}
                          value={from || undefined}
                          onChange={(e) => {
                            setErrors((prevErrors) => ({
                              ...prevErrors,
                              from: false,
                            }))

                            //@ts-ignore
                            setFrom(e.target.value)
                          }}
                        >
                          <option value={undefined} key={undefined}>
                            — Выберите —
                          </option>
                          {cities?.map((item) => (
                            <option value={item.id} key={item.id}>
                              {item.name + ', ' + item.country.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-12 col-sm-6 col-md-6 col-lg-6 mb-3">
                        <small className="small-2 d-block mb-1">Куда</small>
                        <select
                          className={errors.to ? styles.isError : ''}
                          value={to}
                          onChange={(e) => {
                            setErrors((prevErrors) => ({
                              ...prevErrors,
                              to: false,
                            }))

                            setTo(Number(e.target.value))
                          }}
                        >
                          <option value="" key={undefined}>
                            — Выберите —
                          </option>
                          {cities?.map((item) => (
                            <option value={item.id} key={item.id}>
                              {item.name + ', ' + item.country.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-12 col-sm-6 col-md-6 col-lg-6 mb-3">
                        <label
                          htmlFor="report_date_input_tuda"
                          className={classNames(
                            styles.small2,
                            styles.label,
                            'd-block mb-1'
                          )}
                        >
                          Пересечение границы туда
                        </label>
                        <DateTimePicker
                          id="report_date_input_tuda"
                          selectedDate={crossingBorderThere}
                          setSelectedDate={setCrossingBorderThere}
                        />
                      </div>
                      <div className="col-12 col-sm-6 col-md-6 col-lg-6 mb-3">
                        <label
                          htmlFor="report_date_input_obratno"
                          className={classNames(
                            styles.small2,
                            styles.label,
                            'd-block mb-1'
                          )}
                        >
                          Пересечение границы обратно
                        </label>
                        <DateTimePicker
                          id="report_date_input_obratno"
                          selectedDate={crossingBorderBack}
                          setSelectedDate={setCrossingBorderBack}
                        />
                      </div>

                      <>
                        {useMyReportsSentPayload ? (
                          <div className="col-12 mt-3 mb-2">
                            <div className="row">
                              <div className="col-12 col-sm-12 small-2 mt-1 mb-1">
                                <small
                                  className={classNames(
                                    styles.small2,
                                    'd-block mb-1'
                                  )}
                                >
                                  Выдано в рейс
                                </small>
                                <div className="row">
                                  {(issuedByn || issuedBynBalance) && (
                                    <div
                                      className={classNames(
                                        styles.small,
                                        'col-3 pr-0 small'
                                      )}
                                    >
                                      <div className="mb-1">
                                        <b>{issuedByn || 0}</b>{' '}
                                        <span
                                          className={classNames(
                                            styles.small3,
                                            styles.colorGray
                                          )}
                                        >
                                          BYN
                                        </span>
                                      </div>
                                      <div
                                        className={classNames(styles.small3)}
                                      >
                                        Ост.{' '}
                                        <b className={styles.colorRed}>
                                          {formatNumber(+issuedBynBalance) || 0}
                                        </b>{' '}
                                        <span
                                          className={classNames(
                                            styles.small3,
                                            styles.colorRed
                                          )}
                                        >
                                          BYN
                                        </span>
                                      </div>
                                    </div>
                                  )}

                                  {(issuedRub || issuedRubBalance) && (
                                    <div
                                      className={classNames(
                                        styles.small,
                                        'col-3 pr-0 small'
                                      )}
                                    >
                                      <div className="mb-1">
                                        <b>{issuedRub || 0}</b>{' '}
                                        <span
                                          className={classNames(
                                            styles.small3,
                                            styles.colorGray
                                          )}
                                        >
                                          RUB
                                        </span>
                                      </div>
                                      <div
                                        className={classNames(styles.small3)}
                                      >
                                        Ост.{' '}
                                        <b className={styles.colorRed}>
                                          {formatNumber(+issuedRubBalance) || 0}
                                        </b>{' '}
                                        <span
                                          className={classNames(
                                            styles.small3,
                                            styles.colorRed
                                          )}
                                        >
                                          RUB
                                        </span>
                                      </div>
                                    </div>
                                  )}

                                  {(issuedEur || issuedEurBalance) && (
                                    <div
                                      className={classNames(
                                        styles.small,
                                        'col-3 pr-0 small'
                                      )}
                                    >
                                      <div className="mb-1">
                                        <b>{issuedEur || 0}</b>{' '}
                                        <span
                                          className={classNames(
                                            styles.small3,
                                            styles.colorGray
                                          )}
                                        >
                                          EUR
                                        </span>
                                      </div>
                                      <div
                                        className={classNames(styles.small3)}
                                      >
                                        Ост.{' '}
                                        <b className={styles.colorRed}>
                                          {formatNumber(+issuedEurBalance) || 0}
                                        </b>{' '}
                                        <span
                                          className={classNames(
                                            styles.small3,
                                            styles.colorRed
                                          )}
                                        >
                                          EUR
                                        </span>
                                      </div>
                                    </div>
                                  )}

                                  {(issuedUsd || issuedUsdBalance) && (
                                    <div
                                      className={classNames(
                                        styles.small,
                                        'col-3 pr-0 small'
                                      )}
                                    >
                                      <div className="mb-1">
                                        <b>{issuedUsd || 0}</b>{' '}
                                        <span
                                          className={classNames(
                                            styles.small3,
                                            styles.colorGray
                                          )}
                                        >
                                          USD
                                        </span>
                                      </div>
                                      <div
                                        className={classNames(styles.small3)}
                                      >
                                        Ост.{' '}
                                        <b className={styles.colorRed}>
                                          {formatNumber(+issuedUsdBalance) || 0}
                                        </b>{' '}
                                        <span
                                          className={classNames(
                                            styles.small3,
                                            styles.colorRed
                                          )}
                                        >
                                          USD
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-6 col-sm-4 small-2 mt-1 mb-1 pr-1">
                                <small
                                  className={classNames(
                                    styles.small2,
                                    'd-block mb-1'
                                  )}
                                >
                                  Командировочные
                                </small>
                                <div
                                  className={classNames(
                                    styles.inputCurrency,
                                    styles.inputEur,
                                    'mb-1'
                                  )}
                                >
                                  <input
                                    type="text"
                                    value={travelAllowance || ''}
                                    onChange={(e) => {
                                      if (onlyNumberRegularCheck(e)) {
                                        setTravelAllowance(
                                          e.target.value.replace(',', '.')
                                        )
                                      }
                                    }}
                                    placeholder="Сумма"
                                  />
                                </div>
                              </div>
                              <div className="col-6 col-sm-4 small-2 mt-1 mb-1 pl-1 pr-1">
                                <small
                                  className={classNames(
                                    styles.small2,
                                    'd-block mb-1'
                                  )}
                                >
                                  Возмещ. расходов
                                </small>
                                <div
                                  className="report-date"
                                  id="money_date_input_wrapper_reimbursement_my"
                                >
                                  <DateTimePicker
                                    id="reimbursement_date_input_my"
                                    selectedDate={reimbursementDate || ''}
                                    setSelectedDate={setReimbursementDate}
                                    addPosition={true}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="col-12 mt-3 mb-2">
                            <b className="mb-2">Деньги</b>
                            <div
                              className={classNames(
                                styles.small2,
                                'row align-items-start'
                              )}
                            >
                              <div
                                className={classNames(
                                  styles.small2,
                                  'col-12 col-sm-12 mt-1 mb-1'
                                )}
                              >
                                <small
                                  className={classNames(
                                    styles.small2,
                                    'd-block mb-1'
                                  )}
                                >
                                  Выдано в рейс
                                </small>
                                <div className="row">
                                  <div
                                    className={classNames(
                                      styles.small2,
                                      'col-3 pr-1'
                                    )}
                                  >
                                    <div
                                      className={classNames(
                                        styles.inputCurrency,
                                        styles.inputByn,
                                        'mb-1'
                                      )}
                                    >
                                      <input
                                        value={issuedByn}
                                        onChange={(e) => {
                                          if (onlyNumberRegularCheck(e)) {
                                            setIssuedByn(
                                              e.target.value.replace(',', '.')
                                            )
                                          }
                                        }}
                                        type="text"
                                        placeholder="Сумма"
                                      />
                                    </div>
                                    <div className={classNames(styles.small3)}>
                                      Ост.{' '}
                                      <b
                                        className={classNames(styles.colorRed)}
                                      >
                                        {formatNumber(+issuedBynBalance) || 0}
                                      </b>{' '}
                                      <span
                                        className={classNames(styles.colorRed)}
                                      >
                                        BYN
                                      </span>
                                    </div>
                                  </div>

                                  <div
                                    className={classNames(
                                      styles.small2,
                                      'col-3 pl-1 pr-1'
                                    )}
                                  >
                                    <div
                                      className={classNames(
                                        styles.inputCurrency,
                                        styles.inputRub,
                                        'mb-1'
                                      )}
                                    >
                                      <input
                                        value={issuedRub}
                                        onChange={(e) => {
                                          if (onlyNumberRegularCheck(e)) {
                                            setIssuedRub(
                                              e.target.value.replace(',', '.')
                                            )
                                          }
                                        }}
                                        type="text"
                                        placeholder="Сумма"
                                      />
                                    </div>
                                    <div className={classNames(styles.small3)}>
                                      Ост.{' '}
                                      <b
                                        className={classNames(styles.colorRed)}
                                      >
                                        {formatNumber(+issuedRubBalance) || 0}
                                      </b>{' '}
                                      <span
                                        className={classNames(styles.colorRed)}
                                      >
                                        RUB
                                      </span>
                                    </div>
                                  </div>

                                  <div
                                    className={classNames(
                                      styles.small2,
                                      'col-3 pl-1 pr-1'
                                    )}
                                  >
                                    <div
                                      className={classNames(
                                        styles.inputCurrency,
                                        styles.inputEur,
                                        'mb-1'
                                      )}
                                    >
                                      <input
                                        value={issuedEur}
                                        onChange={(e) => {
                                          if (onlyNumberRegularCheck(e)) {
                                            setIssuedEur(
                                              e.target.value.replace(',', '.')
                                            )
                                          }
                                        }}
                                        type="text"
                                        placeholder="Сумма"
                                      />
                                    </div>
                                    <div className={classNames(styles.small3)}>
                                      Ост.{' '}
                                      <b
                                        className={classNames(styles.colorRed)}
                                      >
                                        {formatNumber(+issuedEurBalance) || 0}
                                      </b>{' '}
                                      <span
                                        className={classNames(styles.colorRed)}
                                      >
                                        {' '}
                                        EUR
                                      </span>
                                    </div>
                                  </div>

                                  <div
                                    className={classNames(
                                      styles.small2,
                                      'col-3 pl-1'
                                    )}
                                  >
                                    <div
                                      className={classNames(
                                        styles.inputCurrency,
                                        styles.inputUsd,
                                        'mb-1'
                                      )}
                                    >
                                      <input
                                        value={issuedUsd}
                                        onChange={(e) => {
                                          if (onlyNumberRegularCheck(e)) {
                                            setIssuedUsd(
                                              e.target.value.replace(',', '.')
                                            )
                                          }
                                        }}
                                        type="text"
                                        placeholder="Сумма"
                                      />
                                    </div>
                                    <div className={classNames(styles.small3)}>
                                      Ост.{' '}
                                      <b
                                        className={classNames(styles.colorRed)}
                                      >
                                        {formatNumber(+issuedUsdBalance) || 0}
                                      </b>{' '}
                                      <span
                                        className={classNames(styles.colorRed)}
                                      >
                                        {' '}
                                        USD
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div
                                className={classNames(
                                  styles.small2,
                                  'col-6 col-sm-4 mt-1 mb-1 pr-1'
                                )}
                              >
                                <small
                                  className={classNames(
                                    styles.small2,
                                    'd-block mb-1'
                                  )}
                                >
                                  Командировочные
                                </small>
                                <div
                                  className={classNames(
                                    styles.inputCurrency,
                                    styles.inputEur,
                                    'mb-1'
                                  )}
                                >
                                  <input
                                    type="text"
                                    value={travelAllowance || ''}
                                    onChange={(e) => {
                                      if (onlyNumberRegularCheck(e)) {
                                        setTravelAllowance(
                                          e.target.value.replace(',', '.')
                                        )
                                      }
                                    }}
                                    placeholder="Сумма"
                                  />
                                </div>
                              </div>
                              <div
                                className={classNames(
                                  styles.small2,
                                  'col-6 col-sm-4 mt-1 mb-1 pl-1 pr-1'
                                )}
                              >
                                <small
                                  className={classNames(
                                    styles.small2,
                                    'd-block mb-1'
                                  )}
                                >
                                  Возмещ. расходов
                                </small>
                                <div
                                  className="report-date"
                                  id="money_date_input_wrapper"
                                >
                                  <DateTimePicker
                                    id="money_date_input_wrapper"
                                    selectedDate={reimbursementDate}
                                    setSelectedDate={setReimbursementDate}
                                    addPosition={true}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    </div>

                    {!isDriverDraftLikeMyReports ? (
                      <div className="row mt-2 d-none d-sm-flex">
                        <div
                          className="col-12 col-sm-3 mt-1 mb-1"
                          style={{ textAlign: 'left' }}
                        ></div>
                        {!driverReportsReadOnlySent ? (
                          <div
                            className="col-12 col-sm-6 mt-1 mb-1"
                            style={{ textAlign: 'center' }}
                          >
                            <button
                              onClick={checkValidation}
                              className={classNames(
                                styles.btn,
                                'd-block',
                                'd-sm-inline-block'
                              )}
                            >
                              <i
                                className={classNames(
                                  styles.icon,
                                  styles.iconCheckWhite
                                )}
                              />{' '}
                              Сохранить
                            </button>
                          </div>
                        ) : (
                          <div
                            className="col-12 col-sm-6 mt-1 mb-1"
                            style={{ textAlign: 'center' }}
                          >
                            <span
                              className={classNames(
                                styles.small2,
                                styles.colorGray
                              )}
                            >
                              Путевой отправлен — только просмотр
                            </span>
                          </div>
                        )}
                        <div
                          className="col-12 col-sm-3 mt-1 mb-1"
                          style={{ textAlign: 'right' }}
                        >
                          <div
                            className={classNames(
                              styles.btn,
                              styles.btnSmall,
                              styles.btnBlueOutline,
                              'd-block d-sm-inline-block'
                            )}
                            onClick={() => {
                              dispatch(setIsVisibleModalReport(false))
                              dispatch(setIsVisibleModalReportCommon(false))
                              dispatch(setCurrentReportId(null))
                            }}
                          >
                            Отмена
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="row mt-2 d-none d-sm-flex">
                        <div
                          className="col-12 col-sm-9 mt-1 mb-1"
                          style={{ textAlign: 'left' }}
                        >
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              handleSaveReport(e, status || REPORT_STATUS.draft)
                            }}
                            style={{ marginRight: '10px' }}
                            className={classNames(
                              styles.btn,
                              styles.btnBlue,
                              'mt-1 d-block d-sm-inline-block'
                            )}
                          >
                            Сохранить
                          </button>
                          <button
                            className={classNames(
                              styles.btn,
                              'd-block mt-1 d-sm-inline-block'
                            )}
                            onClick={(e) => {
                              e.preventDefault()
                              handleOpenConfirmModal(e)
                              // e.preventDefault()
                              // handleSentReport(e)
                            }}
                          >
                            <i
                              className={classNames(
                                styles.icon,
                                styles.iconCheckWhite
                              )}
                            ></i>{' '}
                            Отправить
                          </button>
                        </div>
                        <div
                          className="col-12 col-sm-3 mt-1 mb-1"
                          style={{ textAlign: 'right' }}
                        >
                          <div
                            className={classNames(
                              styles.btn,
                              styles.btnSmall,
                              styles.btnBlueOutline,
                              'd-block d-sm-inline-block'
                            )}
                            onClick={() => {
                              dispatch(setIsVisibleModalReport(false))
                              dispatch(setCurrentReportId(null))
                            }}
                          >
                            Отмена
                          </div>
                        </div>
                      </div>
                    )}
                  </fieldset>
                </form>
              )}
              {currentTab === 2 && (
                <AllExpenses readOnly={driverReportsReadOnlySent} />
              )}
              {currentTab === 3 && (
                <AllIncomings
                  readOnly={driverReportsReadOnlySent}
                  issuedByn={issuedByn}
                  issuedRub={issuedRub}
                  issuedEur={issuedEur}
                  issuedBynBalance={issuedBynBalance}
                  issuedRubBalance={issuedRubBalance}
                  issuedEurBalance={issuedEurBalance}
                  issuedUsd={issuedUsd}
                  issuedUsdBalance={issuedUsdBalance}
                />
              )}
              {currentTab === 4 && (
                <CargosTo
                  key={`cargos-${String(reportIdForGroupedCargos ?? 'none')}-fwd`}
                  direction="forward"
                  readOnly={driverReportsReadOnlySent}
                />
              )}
              {currentTab === 5 && (
                <CargosTo
                  key={`cargos-${String(reportIdForGroupedCargos ?? 'none')}-back`}
                  direction="backward"
                  readOnly={driverReportsReadOnlySent}
                />
              )}
            </div>
          ) : (
            <ReportCommonModal ref={reportCommonModalRef} />
          )}
        </div>
      </div>

      {/* Фиксированная панель с кнопками для мобильных устройств - основная модалка */}
      {currentReportTab === '1' && currentTab === 1 && (
        <div className={classNames(styles.winBottomPanel, 'd-block d-sm-none')}>
          <div className="row">
            {isDriverDraftLikeMyReports ? (
              <>
                <div className="col-5 pr-1" style={{ textAlign: 'left' }}>
                  <button
                    className={classNames(
                      styles.btn,
                      styles.btnSmall2,
                      styles.btnWide,
                      'd-block'
                    )}
                    onClick={(e) => {
                      e.preventDefault()
                      handleOpenConfirmModal(e)
                    }}
                  >
                    <i
                      className={classNames(styles.icon, styles.iconCheckWhite)}
                    />{' '}
                    Отправить
                  </button>
                </div>
                <div className="col-4 pl-0 pr-1" style={{ textAlign: 'left' }}>
                  <button
                    className={classNames(
                      styles.btn,
                      styles.btnSmall2,
                      styles.btnWide,
                      styles.btnBlue,
                      'd-block'
                    )}
                    onClick={(e) => {
                      e.preventDefault()
                      handleSaveReport(e, status || REPORT_STATUS.draft)
                    }}
                  >
                    Сохранить
                  </button>
                </div>
                <div className="col-3 pl-0" style={{ textAlign: 'right' }}>
                  <div
                    className={classNames(
                      styles.btn,
                      styles.btnSmall2,
                      styles.btnWide,
                      styles.btnBlueOutline
                    )}
                    onClick={() => {
                      dispatch(setIsVisibleModalReport(false))
                      dispatch(setCurrentReportId(null))
                    }}
                  >
                    Отмена
                  </div>
                </div>
              </>
            ) : (
              <>
                {!driverReportsReadOnlySent ? (
                  <div className="col-6 pr-1" style={{ textAlign: 'left' }}>
                    <button
                      type="button"
                      className={classNames(
                        styles.btn,
                        styles.btnSmall2,
                        styles.btnWide,
                        'd-block'
                      )}
                      onClick={(e) => {
                        e.preventDefault()
                        checkValidation()
                        if (formRef.current) {
                          formRef.current.requestSubmit()
                        }
                      }}
                    >
                      <i
                        className={classNames(
                          styles.icon,
                          styles.iconCheckWhite
                        )}
                      />{' '}
                      Сохранить
                    </button>
                  </div>
                ) : null}
                <div
                  className={classNames(
                    !driverReportsReadOnlySent ? 'col-6 pl-1' : 'col-12',
                    'pr-0'
                  )}
                  style={{ textAlign: 'right' }}
                >
                  <div
                    className={classNames(
                      styles.btn,
                      styles.btnSmall2,
                      styles.btnWide,
                      styles.btnBlueOutline
                    )}
                    onClick={() => {
                      dispatch(setIsVisibleModalReport(false))
                      dispatch(setIsVisibleModalReportCommon(false))
                    }}
                  >
                    Отмена
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Фиксированная панель с кнопками для мобильных устройств - общая модалка */}
      {currentReportTab !== '1' && (
        <div className={classNames(styles.winBottomPanel, 'd-block d-sm-none')}>
          <div className="row">
            <div className="col-5 pr-1" style={{ textAlign: 'left' }}>
              <button
                className={classNames(
                  styles.btn,
                  styles.btnSmall2,
                  styles.btnWide,
                  'd-block'
                )}
                onClick={() => {
                  reportCommonModalRef.current?.handleSave()
                }}
                disabled={!reportCommonModalRef.current?.departure}
              >
                <i className={classNames(styles.icon, styles.iconCheckWhite)} />{' '}
                Сохранить
              </button>
            </div>
            <div className="col-7 pl-0" style={{ textAlign: 'right' }}>
              <div
                className={classNames(
                  styles.btn,
                  styles.btnSmall2,
                  styles.btnWide,
                  styles.btnBlueOutline
                )}
                onClick={() => {
                  dispatch(setIsVisibleModalReportCommon(false))
                }}
              >
                Отмена
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportModal
